import { ReactNode, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import cx from "classnames";
import DOMPurify from "dompurify";

import { CurrencyDataType } from "@/api/CurrencyApi";
import { getCurrencyCode } from "@utils/Currency";
import { stripHtmlTags, unwrapFormControlsToText } from "@utils/Utils";
import { useAppSelector } from "@/store/hooks";
import {
  COMMENTS_FIT_MAX_FONT_SIZE,
  COMMENTS_FIT_MIN_FONT_SIZE,
  getCommentsFitInlineStyles,
  useAutoFitFontSize,
} from "@/hooks/useAutoFitFontSize";

type FooterDocumentInfoProps = {
  comments?: string[]
  suscriptionCurrency: CurrencyDataType,
  exchange_rate: number
  noStripHtml?: boolean

}

const FooterDocumentInfo = (props: FooterDocumentInfoProps) => {
  const { comments = [], suscriptionCurrency, exchange_rate, noStripHtml = false } = props
  const [searchParams] = useSearchParams();
  const isExport = searchParams.get("export") === "1";
  const autoFitCommentsFont = useAppSelector(
    (state) => state.suscription.suscription?.auto_fit_comments_font ?? false
  ) && !isExport;
  const commentsAreaRef = useRef<HTMLDivElement>(null);
  const commentsContentRef = useRef<HTMLDivElement>(null);
  const commentsKey = comments.join("\0");

  const commentsFontSize = useAutoFitFontSize(commentsAreaRef, commentsContentRef, {
    enabled: autoFitCommentsFont,
    maxFontSize: COMMENTS_FIT_MAX_FONT_SIZE,
    minFontSize: COMMENTS_FIT_MIN_FONT_SIZE,
    deps: [autoFitCommentsFont, commentsKey, noStripHtml, suscriptionCurrency.currency_id, exchange_rate],
  });

  const renderComments = (commentList: string[]): ReactNode[] => {
    return commentList.map((c, i) => {
      if (noStripHtml) {
        const sanitized = DOMPurify.sanitize(unwrapFormControlsToText(c), {
          FORBID_TAGS: ["textarea", "input", "select", "button", "option"],
        });
        return (
          <div key={i} dangerouslySetInnerHTML={{ __html: sanitized }} />
        )
      }
      return (
        <p key={i}>
          {stripHtmlTags(c)}
        </p>
      )
    })
  }

  const commentsBody = (
    <>
      Productos con (*) al inicio del codigo son productos exentos o exonerados.
      {(suscriptionCurrency.currency_id != 1) && (
        ` Los montos de esta documento estan expresados en ${getCurrencyCode(`${suscriptionCurrency.currency_id}`)} ( Tipo Cambio: ${exchange_rate.toFixed(2)})`
      )}
      {renderComments(comments)}
    </>
  );

  return (
    <div
      className={cx("invoice-info", {
        "invoice-info--auto-fit-comments": autoFitCommentsFont,
      })}
    >
      {autoFitCommentsFont ? (
        <div ref={commentsAreaRef} className="invoice-info-comments-area">
          <div
            ref={commentsContentRef}
            className="invoice-info-comments-content"
            style={getCommentsFitInlineStyles(commentsFontSize)}
          >
            {commentsBody}
          </div>
        </div>
      ) : (
        commentsBody
      )}
      <div className="signatures">
        <div>
          _____________________<br />
          Nombre de Recibido
        </div>
        <div>
          _____________________<br />
          Firma
        </div>
        <div>
          _____________________<br />
          Identificacion
        </div>
        <div>
          _____________________<br />
          Fecha Recepción
        </div>
      </div>
    </div>
  );
}

export default FooterDocumentInfo;
